/**
 * Desbloqueo con huella (o rostro) en el teléfono.
 *
 * Usa WebAuthn con el autenticador de la plataforma: la huella nunca sale del
 * dispositivo ni la ve la app — el sistema operativo verifica al usuario y
 * devuelve una firma.
 *
 * Qué es y qué no es: sirve para **desbloquear** la sesión ya iniciada sin
 * volver a teclear la contraseña. No reemplaza al inicio de sesión con correo,
 * que es lo que autentica contra el servidor. La primera vez, y cada vez que
 * se cierre sesión de verdad, hay que entrar con correo y contraseña.
 *
 * Requiere HTTPS (o localhost). En Cloudflare Pages se cumple.
 */

import { useCallback, useEffect, useState } from 'react';

const CLAVE = 'xtreme:biometria:v1';

interface Registro {
  /** Identificador de la credencial, en base64url. */
  credencialId: string;
  /** Correo de la sesión a la que quedó asociada. */
  email: string;
  registradaEn: string;
}

// ── base64url ─────────────────────────────────────────────────────────────

function aBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function desdeBase64Url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '=');
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const vista = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) vista[i] = bin.charCodeAt(i);
  return buf;
}

/**
 * Desde TypeScript 5.7 `Uint8Array` es genérico sobre `ArrayBufferLike`, y
 * WebAuthn pide un `BufferSource` respaldado por `ArrayBuffer`. Se fija el
 * tipo para que calce sin castings.
 */
function aleatorio(n = 32): Uint8Array<ArrayBuffer> {
  const a = new Uint8Array(new ArrayBuffer(n));
  crypto.getRandomValues(a);
  return a;
}

// ── Almacenamiento local ──────────────────────────────────────────────────

function leer(): Registro | null {
  try {
    const s = localStorage.getItem(CLAVE);
    return s ? (JSON.parse(s) as Registro) : null;
  } catch {
    return null;
  }
}

function escribir(r: Registro | null) {
  try {
    if (r) localStorage.setItem(CLAVE, JSON.stringify(r));
    else localStorage.removeItem(CLAVE);
  } catch {
    // Sin espacio: la huella simplemente no queda registrada.
  }
}

// ── API ───────────────────────────────────────────────────────────────────

export function useBiometria(emailSesion: string | null) {
  const [soportado, setSoportado] = useState(false);
  const [registro, setRegistro] = useState<Registro | null>(() => leer());
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const hay =
        typeof window !== 'undefined' &&
        window.isSecureContext &&
        typeof PublicKeyCredential !== 'undefined' &&
        (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().catch(
          () => false,
        ));
      if (vivo) setSoportado(Boolean(hay));
    })();
    return () => {
      vivo = false;
    };
  }, []);

  /** ¿Hay una huella registrada para este correo en este dispositivo? */
  const registradaPara = useCallback(
    (email: string | null) => Boolean(registro && email && registro.email === email),
    [registro],
  );

  /** Asocia la huella del dispositivo a la sesión actual. */
  const registrar = useCallback(async (): Promise<void> => {
    if (!soportado) throw new Error('Este dispositivo no tiene huella o rostro disponible.');
    if (!emailSesion) throw new Error('Primero hay que iniciar sesión con correo y contraseña.');

    setOcupado(true);
    try {
      const cred = (await navigator.credentials.create({
        publicKey: {
          challenge: aleatorio(),
          rp: { name: 'Xtreme Mining' },
          user: {
            // El id es local al dispositivo; no viaja a ningún servidor.
            id: aleatorio(16),
            name: emailSesion,
            displayName: emailSesion,
          },
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 }, // ES256
            { type: 'public-key', alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'preferred',
          },
          timeout: 60_000,
          attestation: 'none',
        },
      })) as PublicKeyCredential | null;

      if (!cred) throw new Error('No se pudo registrar la huella.');

      const nuevo: Registro = {
        credencialId: aBase64Url(cred.rawId),
        email: emailSesion,
        registradaEn: new Date().toISOString(),
      };
      escribir(nuevo);
      setRegistro(nuevo);
    } catch (e) {
      const nombre = (e as { name?: string })?.name;
      if (nombre === 'NotAllowedError') throw new Error('Se canceló el registro de la huella.');
      throw e instanceof Error ? e : new Error('No se pudo registrar la huella.');
    } finally {
      setOcupado(false);
    }
  }, [soportado, emailSesion]);

  /** Pide la huella para desbloquear. Devuelve `true` si el usuario se verificó. */
  const verificar = useCallback(async (): Promise<boolean> => {
    const r = leer();
    if (!r) return false;

    setOcupado(true);
    try {
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: aleatorio(),
          allowCredentials: [
            {
              type: 'public-key',
              id: desdeBase64Url(r.credencialId),
              transports: ['internal'],
            },
          ],
          userVerification: 'required',
          timeout: 60_000,
        },
      });
      return assertion !== null;
    } catch (e) {
      const nombre = (e as { name?: string })?.name;
      // Cancelar no es un fallo del sistema: se vuelve a la contraseña.
      if (nombre === 'NotAllowedError') return false;
      throw e instanceof Error ? e : new Error('No se pudo verificar la huella.');
    } finally {
      setOcupado(false);
    }
  }, []);

  const olvidar = useCallback(() => {
    escribir(null);
    setRegistro(null);
  }, []);

  return {
    /** El dispositivo tiene huella o rostro y estamos en contexto seguro. */
    soportado,
    /** Hay una credencial guardada en este dispositivo. */
    registrada: Boolean(registro),
    emailRegistrado: registro?.email ?? null,
    registradaPara,
    ocupado,
    registrar,
    verificar,
    olvidar,
  };
}
